// apiService.js

import { QueryClient } from '@tanstack/react-query';
import { getSalesList, GetSalesListParams, getSalesManagers } from '../api/membershipPayments';

const queryClient = new QueryClient();

export const fetchGetSalesList = (params: GetSalesListParams, authorization: string) => ({
    queryKey: ['getSalesListQuery', params.centerId, params.startDate, params.endDate, params.userId, params.role],
    queryFn: () =>
        getSalesList({
            centerId: params.centerId,
            startDate: params.startDate,
            endDate: params.endDate,
            userId: params.userId,
            role: params.role,
            accessToken: authorization,
        }),
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.data ?? [];
    },
});

export const fetchGetSalesManagers = (params: { centerId: string }, authorization: string) => ({
    queryKey: ['getSalesManagersQuery', params.centerId],
    queryFn: () => getSalesManagers(params.centerId, authorization),
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.data?.salesManagers ?? [];
    },
});

export const refreshGetSaleManagers = () => {
    queryClient.invalidateQueries({
        queryKey: ['getSalesManagersQuery'],
    });
};

// 판매 상태 타입
export type SalesStatus = 'COMPLETED' | 'UPGRADED' | 'PARTIAL_REFUND' | 'FULL_REFUND' | 'PARTIAL_PAYMENT';

// 판매 담당자 필터 타입
type SalesManagerNames = SalesManager[];

// 요약 데이터 타입
export interface SalesListSummaryData {
    totalSales: number; // 총 매출액
    totalSalesCount: number; // 총 매출 건수
    totalSalesMemberCount: number; // 총 매출 명수
    newPaymentAmount: number; // 신규 결제액
    newPaymentCount: number; // 신규 결제 건수
    newPaymentMemberCount: number; // 신규 결제 명수
    reRegistrationPaymentAmount: number; // 재등록 결제액
    reRegistrationPaymentCount: number; // 재등록 결제 건수
    reRegistrationPaymentMemberCount: number; // 재등록 결제 명수
    cancelSales: number; // 환불 금액
    cancelSalesCount: number; // 환불 건수
    cancelSalesMemberCount: number; // 환불 명수
    partialPaymentAmount: number; // 부분 결제 금액
    partialPaymentCount: number; // 부분 결제 건수
    partialPaymentMemberCount: number; // 부분 결제 명수
}

// 테이블 데이터 타입
export interface MembershipPaymentData {
    /** 레거시 매출 원장 식별자 */
    _id?: string;
    membershipId: string; // 회원권 ID
    /** 매출 원장 식별자 — 백엔드 응답 버전에 따라 둘 중 하나가 내려올 수 있다. */
    membershipPaymentId?: string;
    salesRecordId?: string;
    /** 그룹 수강권 원본 식별자 */
    memberTicketId?: string;
    /**
     * 인계 이후 실제 환불·회차 반영 대상.
     * 원매출의 membershipId는 과거 수강권을 가리킬 수 있으므로 값이 있으면 이를 우선한다.
     */
    currentMembershipId?: string;
    currentMemberTicketId?: string;
    originalMembershipId?: string;
    saleStatus: SalesStatus; // 판매 상태
    paymentDate: string; // 결제 일시
    salesManager: SalesManager;
    coachId?: string; // 판매 당시 담당 강사 ID
    coachName: string; // 담당 강사 이름
    currentCoachId?: string; // 현재 담당 강사 ID
    currentCoachName?: string; // 현재 담당 강사 이름
    memberName: string; // 회원 이름
    memberId: string; // 회원 ID
    membershipTemplateId: string; // 수강권 필터를 위한 수강권 메뉴 ID
    isNewSales: boolean; // 신규 여부
    source?: 'pt' | 'group'; // 매출 출처 — 서버 합산 응답에 포함 (미수금/정산 통합)
    totalPrice: number; // 판매 금액
    paymentAmount: number; // 결제 금액
    cancelAmount: number; // 취소 금액
    totalSession: number; // 총 회차
    totalSessionOffset: number; // 총 회차 변화량
    totalCount?: number; // 그룹 수강권 총 회차
    totalCountOffset?: number; // 그룹 수강권 총 회차 변화량
    membershipName: string; // 수강권 이름
    activationDate: string; // 이용기간 시작일
    expirationDate: string; // 이용기간 종료일
    /** 서버가 전체 결제·환불 원장을 반영해 계산한 값. 미제공 시 환불을 열지 않는다. */
    refundableAmount?: number;
    canRefund?: boolean;
    refundBlockReason?: string;
    /** PT 원매출 행의 판매자 정정 capability. 구서버에서 미제공하면 버튼을 숨긴다. */
    salesManagerEditable?: boolean;
    /** salesManagerEditable=false일 때 서버가 내려주는 차단 코드. */
    salesManagerEditBlockReason?: string;
}

// 필터 - 회원 리스트 타입
interface MemberFilter {
    memberId: string;
    memberName: string;
}

// 필터 - 수강권 리스트 타입
interface MembershipTemplateFilter {
    membershipTemplateId: string;
    membershipTemplateName: string;
}

// 필터 데이터 타입
interface Filters {
    memberList: MemberFilter[]; // 회원 이름 검색 필터
    membershiptemplateList: MembershipTemplateFilter[]; // 수강권 이름 필터
}

// 전체 데이터 타입
export interface SalesDataResponse {
    salesManagers: SalesManager[]; // 판매 담당자 필터
    summaryData: SalesListSummaryData; // 요약 데이터
    tableDataByMembershipPayment: MembershipPaymentData[]; // 테이블 데이터
    filters: Filters; // 필터
}

export type SalesManager = {
    role: 'coach' | 'etc' | 'all';
    userId: string;
    userName: string;
    isDeleted?: boolean;
};
